import { useEffect, useState } from 'react';
import { BasePartialDataOptions, BasePartialDetailsState, AvailableKeys, AdditionalParams } from 'shared/types';

type InitialStateLoader = () => unknown;
type PartialDataLoaderFn<T> = (options: BasePartialDataOptions<T>) => Promise<Record<string, any>>;

export interface PartialDataLoaderOptions<T> {
	keys: AvailableKeys<T>;
	initialState: unknown | InitialStateLoader;
	dataLoaderFn: PartialDataLoaderFn<T>;
}

export interface PartialDataLoaderState<T> {
	partialData: T;
	isLoading: boolean;
}

export default function usePartialDataLoader<T extends BasePartialDetailsState>(options: PartialDataLoaderOptions<T>) {
	const initialState = typeof options.initialState === 'function' ? options.initialState() : options.initialState;
	const [partialData, setPartialData] = useState<T>(initialState);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	/**
	 * Helper function to determine if the given chunk of partial (by key) data has been already loaded or if it is still loading.
	 */
	function isKeyLoading(key: keyof T): boolean {
		if (partialData === undefined || partialData === null) {
			return isLoading;
		}

		if (Object.keys(partialData).includes(key.toString()) && partialData[key] !== undefined) {
			return false;
		}

		return isLoading;
	}

	// we'd then make a request to the server to get the data for the missing keys.
	useEffect(() => {
		const providedKeys = Object.keys(partialData);
		const requiredKeys = options.keys.map((literal) => literal.valueOf().toString()) as Array<keyof T>;
		const missingKeys = requiredKeys.filter((value) => providedKeys.indexOf(value.valueOf().toString()) === -1);
		loadData(partialData.id, missingKeys, partialData.additionalParams);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	async function loadData(id: string, missingKeys: Array<keyof T>, additionalParams: AdditionalParams | undefined) {
		try {
			// await timeout(1000);
			console.debug('Fetching missing data:', missingKeys);
			const missingData = await options.dataLoaderFn({ id, missingKeys, additionalParams });

			console.debug('Missing data loaded:', missingData);
			setPartialData({
				...partialData,
				...missingData,
			});
		} catch (error) {
			console.error('Unable to fetch remaining data.');
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	}

	return {
		partialData,
		isLoading: isKeyLoading,
		isLoadingAny: (keys: Array<keyof T> = []) => keys.some((key) => isKeyLoading(key)),
		hasError,
		setHasError,
	};
}
